import { useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { App, Project } from '@hangar/shared';
import { useApps } from '@/api/hooks';
import { stagger } from '@/lib/motion';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ProfilesPanel } from '@/features/profiles/ProfilesPanel';
import { AppCard, AppCardSkeleton } from './AppCard';
import { appsCountLabel, appsViewState, selectedApp } from './appsSectionModel';
import { useAppActions, type AppActions } from './useAppActions';

/** The selection lives in the URL; no hash (a pick is not a deep link) and no scroll reset. */
const pickTarget = (projectSlug: string, appSlug?: string) => ({ to: '/projects/$slug' as const, params: { slug: projectSlug }, search: appSlug ? { app: appSlug } : {}, replace: true, resetScroll: false });

/** The selected app (`?app=<appSlug>`), how to change it, and the connect/edit/archive actions. */
function useAppsSelection(project: Project) {
  const apps = useApps(project.id);
  const search = useSearch({ strict: false }) as { app?: string; connect?: true };
  const navigate = useNavigate();
  const selected = selectedApp(apps.data, search.app);
  const select = (app: App) => void navigate(pickTarget(project.slug, app.slug));
  const actions = useAppActions({
    project,
    onConnected: select,
    // The archived app leaves the list: the selection falls back to the first app.
    onArchived: (app) => (selected?.id === app.id ? void navigate(pickTarget(project.slug)) : undefined),
  });
  useConnectFromUrl(project, search, actions);
  return { apps, selected, select, actions };
}

/** `?connect=1` (Overview's and Getting started's "New app"): opens Connect app once, for someone who may, then drops the flag. */
function useConnectFromUrl(project: Project, search: { app?: string; connect?: true }, actions: AppActions) {
  const navigate = useNavigate();
  const { connect } = actions;
  const allowed = actions.can.allowed;
  useEffect(() => {
    if (!search.connect) return;
    if (allowed) connect();
    void navigate(pickTarget(project.slug, search.app));
    // Once per arrival (`connect` is a new function every render; the flag leaves the URL right away).
  }, [search.connect, allowed]);
}

type Selection = ReturnType<typeof useAppsSelection>;

/** "Connect app" opens the connect sheet: the page's one primary action (the header's, or the empty state's when there is no app). */
function ConnectButton({ actions, testId }: { actions: AppActions; testId: string }) {
  return (
    <GatedButton can={actions.can} data-testid={testId} onClick={actions.connect}>
      <Icon name="add" size={18} />
      Connect app
    </GatedButton>
  );
}

function AppsLoading() {
  return (
    <div className="flex flex-col gap-3 p-4 sm:p-5" aria-busy="true" aria-label="Loading apps">
      {[0, 1].map((i) => (
        <AppCardSkeleton key={i} />
      ))}
    </div>
  );
}

function AppsEmpty({ project, actions }: { project: Project; actions: AppActions }) {
  return (
    <EmptyState
      testId="apps-empty"
      icon="apps"
      title="No apps connected yet"
      description={`Connect an Expo, React Native or native app to ${project.name} to start building through Hangar.`}
      action={<ConnectButton actions={actions} testId="apps-empty-connect" />}
    />
  );
}

function AppsList({ project, s, apps }: { project: Project; s: Selection; apps: App[] }) {
  return (
    <ul className="flex flex-col gap-3 p-4 sm:p-5">
      {apps.map((app, i) => (
        <AppCard key={app.id} app={app} projectSlug={project.slug} index={i} selected={s.selected?.id === app.id} onSelect={() => s.select(app)} actions={s.actions.actionsFor(app)} />
      ))}
    </ul>
  );
}

/** The card's body: skeletons while loading, the error, the empty state, or the cards. */
function AppsBody({ project, s }: { project: Project; s: Selection }) {
  const state = appsViewState(s.apps);
  if (state === 'loading') return <AppsLoading />;
  if (state === 'error') return <ErrorState compact error={s.apps.error} onRetry={() => void s.apps.refetch()} />;
  if (state === 'empty' || !s.apps.data) return <AppsEmpty project={project} actions={s.actions} />;
  return <AppsList project={project} s={s} apps={s.apps.data} />;
}

interface CardProps {
  project: Project;
  s: Selection;
}

function AppsCardHeader({ s }: Pick<CardProps, 's'>) {
  return (
    // From lg this card is the narrow left column next to the profiles: stack the action under the text.
    <CardHeader className="lg:has-data-[slot=card-action]:grid-cols-1">
      <CardTitle>Applications{appsCountLabel(s.apps.data)}</CardTitle>
      <CardDescription>Expo, React Native and native apps that build through Hangar</CardDescription>
      {/* No app yet: the empty state carries the one Connect app button. */}
      {appsViewState(s.apps) === 'empty' ? null : (
        <CardAction className="lg:col-start-1 lg:row-span-1 lg:row-start-3 lg:mt-3 lg:justify-self-start">
          <ConnectButton actions={s.actions} testId="apps-connect" />
        </CardAction>
      )}
    </CardHeader>
  );
}

function AppsCard({ project, s }: CardProps) {
  return (
    // min-w-0: a long app name must truncate inside the single-column grid on phones, never widen the track.
    <Card className="enter min-w-0" style={stagger(1)}>
      <AppsCardHeader s={s} />
      <CardContent className="p-0 sm:p-0">
        <AppsBody project={project} s={s} />
      </CardContent>
    </Card>
  );
}

/** The selected app's build profiles. The shadow mode card returns with docs/mvp-plan.md P18 (ShadowCard). */
function SelectedAppPanels({ app, project }: { app: App | undefined; project: Project }) {
  if (!app) return null;
  return (
    <div className="flex min-w-0 flex-col gap-5">
      <ProfilesPanel key={app.id} app={app} project={project} />
    </div>
  );
}

/**
 * Screen 09: applications as selectable cards (left column on desktop) + the selected app's build profiles. "Connect
 * app" opens the connect sheet (screen 15, useAppActions, shared with Getting started) and selects the new app; each
 * card has Edit and Archive. The selection lives in the URL (`?app=<appSlug>`, replaced — not pushed — on every pick;
 * the first app when absent or unknown).
 */
export function AppsSection({ project }: { project: Project }) {
  const s = useAppsSelection(project);
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(300px,1fr)_minmax(0,2.6fr)] lg:items-start">
      <AppsCard project={project} s={s} />
      <SelectedAppPanels app={s.selected} project={project} />
      {s.actions.overlays}
    </div>
  );
}
